import { useSettingVerticalizedImageryInSignUp } from "@faire/retailer-visitor-shared/settings/useSettingVerticalizedImageryInSignUp";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { getPreSignUpActivities } from "@faire/retailer-visitor-shared/views/SignUp/_utils/getPreSignUpActivities";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSignUpStepsStore } from "@faire/retailer-visitor-shared/views/SignUp/SignUpStepsContext";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { useMutation as useSignUpVerticalizedImageMutation } from "@faire/web-api--source/endpoints/www/api/retailer/sign-up-verticalized-image/post-hooks";
import { trackSignupVerticalizedImageFetchError } from "@faire/web-api--source/events/signup/error/verticalizedImageFetch";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISignUpVerticalizedImageRequest } from "@faire/web-api--source/indigofair/data/ISignUpVerticalizedImageRequest";
import { ISignUpVerticalizedImageResponse } from "@faire/web-api--source/indigofair/data/ISignUpVerticalizedImageResponse";
import { ISlateTextElement } from "@faire/web-api--source/indigofair/data/ISlateTextElement";
import { ISlateTextStyle } from "@faire/web-api--source/indigofair/data/ISlateTextStyle";
import { IVerticalizedImageryInSignUpXP } from "@faire/web-api--source/indigofair/data/IVerticalizedImageryInSignUpXP";
import { TextStyleType } from "@faire/web-api--source/indigofair/data/TextStyleType";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { useEffect, useState } from "react";

export enum SignUpImage {
  WELCOME = "welcome",
  VERTICALIZED = "verticalized",
  TERMS_AND_OWF = "terms_and_owf",
}

const getSignUpImageFromStep = (currentStep: Step): SignUpImage => {
  switch (currentStep) {
    case Step.PERSONAL_INFO:
    case Step.BUSINESS_TYPE:
    case Step.STORE_INFO:
    case Step.STORE_TYPE_S1:
      return SignUpImage.WELCOME;

    case Step.OPENING_YEAR:
    case Step.OPENING_MONTH:
    case Step.ANNUAL_SALES:
    case Step.RESELLER_CERT:
    case Step.FAIRE_DIRECT_SURVEY:
      return SignUpImage.VERTICALIZED;

    case Step.SELECT_NET_TERMS:
    case Step.OWF_UPSELL:
    case Step.OWF_APPLICATION:
    case Step.END:
      return SignUpImage.TERMS_AND_OWF;

    default:
      return SignUpImage.WELCOME;
  }
};

const useWelcomeImage = (strictLocalize: StrictLocalizeFunction) => {
  const verticalizedImageryTreatment = useSettingVerticalizedImageryInSignUp();
  if (
    verticalizedImageryTreatment ===
    IVerticalizedImageryInSignUpXP.Bucket.TREATMENT_B
  ) {
    return ISignUpVerticalizedImageResponse.build({
      image: IImage.build({
        url: "https://cdn.faire.com/fastly/7699f6734eb3a4620aa405b7b997eb6bb9cbdb72553b29485e4a4976835c2bcc.jpeg",
        width: 720,
        height: 876,
      }),
      image_description: [
        ISlateTextElement.build({
          text: "Hackney Essentials",
          style: ISlateTextStyle.build({
            type: TextStyleType.PARAGRAPH,
            color: ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE,
            decorators: [ISlateTextStyle.TextDecorator.EMPHASIS],
          }),
        }),
        ISlateTextElement.build({
          text: strictLocalize({
            defaultMessage: "London, UK",
            description: {
              text: "Location of store in image",
              img: "https://cdn.faire.com/fastly/a3a5804c6c324211a3f3a5552f724db27248dea2d98b85570d8e662ebb8c7e9c.png",
            },
          }),
          style: ISlateTextStyle.build({
            type: TextStyleType.PARAGRAPH,
            color: ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE,
          }),
        }),
      ],
    });
  }
  return ISignUpVerticalizedImageResponse.build({
    image: IImage.build({
      url: "https://cdn.faire.com/fastly/bad29e1c7b2c90b1b68099091e1cbfb6570a27fbf73ba55627387229c27c560e.jpeg",
      width: 720,
      height: 876,
    }),
    image_description: [
      ISlateTextElement.build({
        text: "Luna & Curious",
        style: ISlateTextStyle.build({
          type: TextStyleType.PARAGRAPH,
          color: ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE,
          decorators: [ISlateTextStyle.TextDecorator.EMPHASIS],
        }),
      }),
      ISlateTextElement.build({
        text: strictLocalize({
          defaultMessage: "London, UK",
          description: {
            text: "Location of store in image",
            img: "https://cdn.faire.com/fastly/58fd932e8b013a3ede59823e351dc4ccd157127fb803283f576febf5095b3db7.png",
          },
        }),
        style: ISlateTextStyle.build({
          type: TextStyleType.PARAGRAPH,
          color: ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE,
        }),
      }),
    ],
  });
};

export const getTermsAndOwfImage = (strictLocalize: StrictLocalizeFunction) =>
  ISignUpVerticalizedImageResponse.build({
    image: IImage.build({
      url: "https://cdn.faire.com/fastly/490dac2b545152c3d77479979f3415b62d35efb187249a68651c437f169ca069.jpeg",
      width: 720,
      height: 876,
    }),
    image_description: [
      ISlateTextElement.build({
        text: "AB Fits",
        style: ISlateTextStyle.build({
          type: TextStyleType.PARAGRAPH,
          color: ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE,
          decorators: [ISlateTextStyle.TextDecorator.EMPHASIS],
        }),
      }),
      ISlateTextElement.build({
        text: strictLocalize({
          defaultMessage: "San Francisco, California",
          description: {
            text: "Location of store in image",
            img: "https://cdn.faire.com/fastly/09695fac2e0a33bd6a4a2926adcb31b8dcf5c44ad593f1369c96f2ddd7fd4dba.png",
          },
        }),
        style: ISlateTextStyle.build({
          type: TextStyleType.PARAGRAPH,
          color: ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE,
        }),
      }),
    ],
  });

export const useSignUpImage = (): {
  signUpImages: Record<SignUpImage, ISignUpVerticalizedImageResponse>;
  currentSignUpImage: SignUpImage;
  imagesToReload: SignUpImage[];
  setImagesToReload: (images: SignUpImage[]) => void;
} => {
  const { strictLocalize } = useStrictLocalization();
  const { mutateAsync } = useSignUpVerticalizedImageMutation();
  const [
    { matched_business, user, website_url, country, postal_code, style_quiz },
  ] = useSignUpRequest([
    "matched_business",
    "user",
    "website_url",
    "country",
    "postal_code",
    "style_quiz",
  ]);
  const { queryParams } = useSignUpQueryParams();
  const [{ currentStep }] = useSignUpStepsStore(["currentStep"]);
  const welcomeImage = useWelcomeImage(strictLocalize);
  const isVerticalizedSignUpEnabled = useSettingVerticalizedImageryInSignUp();
  const [signUpImages, setSignUpImages] = useState<
    Record<SignUpImage, ISignUpVerticalizedImageResponse>
  >({
    [SignUpImage.WELCOME]: welcomeImage,
    [SignUpImage.VERTICALIZED]: welcomeImage, // default placeholder before image is fetched
    [SignUpImage.TERMS_AND_OWF]: getTermsAndOwfImage(strictLocalize),
  });
  const [currentSignUpImage, setCurrentSignUpImage] = useState<SignUpImage>(
    SignUpImage.WELCOME
  );
  const imageAtCurrentStep = getSignUpImageFromStep(currentStep);
  // track if store type changed and verticalized image should be reloaded
  const [imagesToReload, setImagesToReload] = useState<SignUpImage[]>([]);

  useEffect(() => {
    setImagesToReload([SignUpImage.VERTICALIZED]);
  }, [style_quiz, matched_business]);

  useEffect(() => {
    if (
      currentSignUpImage === imageAtCurrentStep ||
      !isVerticalizedSignUpEnabled
    ) {
      return;
    }

    const fetchImage = async () => {
      try {
        if (imageAtCurrentStep === SignUpImage.VERTICALIZED) {
          const response = await mutateAsync([
            ISignUpVerticalizedImageRequest.build({
              matched_business,
              email_address: user?.email_address,
              retailer_store_name: matched_business?.name,
              website_url,
              country: country as keyof typeof ISO3166Alpha3,
              postal_code,
              style_quiz,
              pre_sign_up_activities: getPreSignUpActivities(
                queryParams.source
              ),
            }),
          ]);

          if (response) {
            setSignUpImages((prev) => ({
              ...prev,
              [SignUpImage.VERTICALIZED]: response,
            }));
          }
        }

        setCurrentSignUpImage(imageAtCurrentStep);
      } catch (err) {
        trackSignupVerticalizedImageFetchError();
      }
    };

    fetchImage();
  }, [
    imageAtCurrentStep,
    currentSignUpImage,
    mutateAsync,
    matched_business,
    user?.email_address,
    website_url,
    country,
    postal_code,
    style_quiz,
    queryParams.source,
    strictLocalize,
    isVerticalizedSignUpEnabled,
  ]);

  return {
    signUpImages,
    currentSignUpImage,
    imagesToReload,
    setImagesToReload,
  };
};
